import React from "react"
import type { SubmitHandler } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { Link, useNavigate } from "react-router"

import { PageCard } from "@/components/page-card/page-card"
import { PageTitle } from "@/components/page-title"
import type { User } from "@/models/user"
import type { EditUserSchema } from "@/modules/admin/users"
import {
  UserIdentityFields,
  UserOrganizationFields,
  editUserSchema,
  normalizeOrganizationIds,
  useConfirmDeleteUser,
} from "@/modules/admin/users"
import { adminEntityFieldsMaxWidthSx } from "@/modules/admin/utils/admin-entity-fields-max-width-sx"
import { Form } from "@/nzds/forms/form/form"
import { ResetButton } from "@/nzds/forms/reset-button/reset-button"
import { SubmitButton } from "@/nzds/forms/submit-button/submit-button"
import { useForm } from "@/nzds/forms/use-form/use-form"
import { useUpdateUser } from "@/services/user"
import { useUrl } from "@/utils/url"

import { Link as MuiLink, Stack } from "@mui/material"
import Button from "@mui/material/Button"

interface AdminUserEditFormProps {
  canDeleteUser: boolean
  user: User
  userId: number
  userIsFetching: boolean
}

export const AdminUserEditForm: React.FC<AdminUserEditFormProps> = ({
  canDeleteUser,
  user,
  userId,
  userIsFetching,
}) => {
  const navigate = useNavigate()
  const { t } = useTranslation("common")
  const { adminUserPath, adminUsersPath } = useUrl()

  const form = useForm({
    schema: editUserSchema,
    defaultValues: {
      firstName: user.firstName ?? "",
      lastName: user.lastName ?? "",
      email: user.email ?? "",
      organizationIds: normalizeOrganizationIds(user.organizationIds ?? []),
      role: user.role,
    },
    mode: "onChange",
  })

  const navigateToShow = () => {
    void navigate(adminUserPath(String(userId)), { replace: true })
  }

  const { updateUserMutateAsync, updateUserIsPending } = useUpdateUser({
    onSuccess: () => {
      navigateToShow()
    },
  })

  const { confirmAndDeleteUser, deleteUserIsPending } = useConfirmDeleteUser({
    onSuccess: () => {
      void navigate(adminUsersPath, { replace: true })
    },
  })

  const handleSubmit: SubmitHandler<EditUserSchema> = async (values) => {
    await updateUserMutateAsync({
      userId: String(userId),
      user: {
        first_name: values.firstName,
        last_name: values.lastName,
        email: values.email,
        role: values.role,
        organization_ids: values.organizationIds,
      },
    })
  }

  const handleDeleteClick = () => {
    confirmAndDeleteUser({
      firstName: user.firstName,
      id: String(userId),
      lastName: user.lastName,
    }).catch(() => undefined)
  }

  const areFieldsDisabled =
    userIsFetching || updateUserIsPending || deleteUserIsPending

  return (
    <Form<EditUserSchema>
      aria-label={t("editUser.title")}
      form={form}
      onSubmit={handleSubmit}
    >
      <Stack sx={{ gap: 2 }}>
        <PageTitle
          breadcrumbActions={
            <>
              <MuiLink component={Link} to={adminUsersPath} underline="hover">
                {t("admin.users")}
              </MuiLink>
              <MuiLink
                component={Link}
                to={adminUserPath(String(userId))}
                underline="hover"
              >
                {t("admin.userDetail.viewUser")}
              </MuiLink>
            </>
          }
          heading={t("editUser.title")}
          primaryActions={
            <>
              <ResetButton
                disabled={areFieldsDisabled}
                onClick={navigateToShow}
                variant="outlined"
              >
                {t("button.cancel")}
              </ResetButton>
              <SubmitButton disabled={areFieldsDisabled}>
                {t("button.save")}
              </SubmitButton>
            </>
          }
          secondaryActions={
            canDeleteUser ? (
              <Button
                color="error"
                disabled={areFieldsDisabled}
                onClick={handleDeleteClick}
              >
                {t("button.delete")}
              </Button>
            ) : undefined
          }
        />
        <PageCard>
          <Stack sx={{ ...adminEntityFieldsMaxWidthSx, gap: 2 }}>
            <UserIdentityFields disabled={areFieldsDisabled} />
            <UserOrganizationFields disabled={areFieldsDisabled} />
          </Stack>
        </PageCard>
      </Stack>
    </Form>
  )
}
